import ReactWebChat, { createDirectLine, createStore } from "botframework-webchat";
import markdownit from "markdown-it";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useLocation } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import Globe from "../asset/Globe.svg";
import styleOptions from "../asset/js/styleSet.js";
import Send from "../asset/newSend.png";
import utils from "../helpers/Utils";
import Header from "./Header";
import "./WebChat.css";

const LANGUAGE_QUERY_PARAMS = ["lang"];
const CHANNEL_QUERY_PARAMS = ["channel"];

const WebChat = (factory, deps) => {
  const { search } = useLocation();
  const md = useMemo(() => markdownit(), []);
  const urlParams = useMemo(() => new URLSearchParams(search), [search]);
  const userLocale = useMemo(() => {
    const browserLocale = navigator.language || navigator.userLanguage || "it-IT";
    const languageParam = LANGUAGE_QUERY_PARAMS
      .map((paramName) => urlParams.get(paramName))
      .find((value) => value && value.trim());

    return languageParam ? languageParam.trim().replace(/_/g, "-") : browserLocale;
  }, [urlParams]);
  const sourceChannel = useMemo(() => {
    const channelParam = CHANNEL_QUERY_PARAMS
      .map((paramName) => urlParams.get(paramName))
      .find((value) => value && value.trim());

    return channelParam ? channelParam.trim() : undefined;
  }, [urlParams]);

  //initialize directLine state

  const [styleSet, setStyleOptions] = useState();
  const [session, setSession] = useState();
  const [showDialog, setShowDialog] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogMessage, setDialogMessage] = useState("");

  useMemo(() => setStyleOptions(styleOptions()), []);
  
  const linkifyJuliaPortal = useCallback((text) => {
    const input = text || "";

    if (!input.includes("julia.comune.roma.it")) {
      return input;
    }

    return input.replace(
      /(^|[^(])(https:\/\/julia\.comune\.roma\.it\/?)(?=(?:\s|$|[)\]}>.,!?;:]))/g,
      (_full, prefix, url) => {
        const normalized = url.endsWith("/") ? url : `${url}/`;
        return `${prefix}[${url}](${normalized})`;
      }
    );
  }, []);

  const renderBotMarkdown = useCallback(
    (markdown) => md.render(linkifyJuliaPortal(markdown)).replace(/<a\s+/g, '<a target="_blank" rel="noopener noreferrer" '),
    [md, linkifyJuliaPortal]
  );

  const initConversation = useCallback(() => {
    let myuuid = uuidv4();
    if (getCookie("userId") == null) document.cookie = "userId=" + myuuid + "; SameSite=None; Secure";

    // var timer = setTimeout(() => {
    //     clearTimeout(timer);
    //     onReset();
    // }, 3600000);

    setSession(false);

    (async function () {
      const resp = await fetchToken();
      const token = resp.token;
      const respSpeech = await fetchTokenSpeech();
      const tokenSpeech = respSpeech.token;
      const region = respSpeech.region;
      const key = Date.now();

      async function createHybridPonyfillFactory() {
        // Create the ponyfill factory function, which can be called to create a concrete implementation of the ponyfill.
        const speechServicesPonyfillFactory = await window.WebChat.createCognitiveServicesSpeechServicesPonyfillFactory({
          // We are passing the Promise function to the "credentials" field.
          // This function will be called every time the token is used.
          credentials: {
            region: region,
            authorizationToken: tokenSpeech,
          },
        });
        const webSpeechPonyfillFactory = await window.WebChat.createBrowserWebSpeechPonyfillFactory();

        return (options) => {
          // We are using two ponyfill factories to create two concrete ponyfills.
          const speechServicesPonyfill = speechServicesPonyfillFactory(options);
          const webSpeechPonyfill = webSpeechPonyfillFactory(options);
          // And we are merging the result from two different ponyfills.
          // We use browser-supported speech for speech-to-text, and Speech Services for text-to-speech.
          return {
            SpeechGrammarList: webSpeechPonyfill.SpeechGrammarList,
            SpeechRecognition: webSpeechPonyfill.SpeechRecognition,
            // Turn text-to-speech on/off. Return null for off.
            // speechSynthesis: speechServicesPonyfill.speechSynthesis,
            // SpeechSynthesisUtterance: speechServicesPonyfill.SpeechSynthesisUtterance
            speechSynthesis: null,
            SpeechSynthesisUtterance: null,
          };
        };
      }

      setSession({
        directLine: createDirectLine({
          domain:"https://europe.directline.botframework.com/v3/directline",
          token: token,
          //secret:"",
          conversationId: getCookie("conversationId"),
          webSocket: true,
          watermark: "0",
        }),
        selectVoice: (voices, activity) => {
          return activity.locale === "it-IT"
            ? voices.find(({ name }) => /IsabellaNeural/iu.test(name))
            : voices.find(({ name }) => /IsabellaNeural/iu.test(name)) || voices.find(({ name }) => /IsabellaNeural/iu.test(name));
        },
        key,
        webSpeechPonyfillFactory: await createHybridPonyfillFactory(),
        store: createStore({}, ({ dispatch }) => (next) => (action) => {
          if (action.type === "WEB_CHAT/SET_SEND_BOX") {
            checkInput();
            return next(action);
          }
          if (action.type === "DIRECT_LINE/CONNECT_FULFILLED" && getCookie("conversationId") === null) {
            dispatch({
              type: "WEB_CHAT/SEND_EVENT",
              payload: {
                name: "webchat/join",
                locale: userLocale,
                value: { locale: userLocale, channel: sourceChannel },
              },
            });
          } else if (action.type === "DIRECT_LINE/INCOMING_ACTIVITY") {
            addGeolocationBtn();
            addSendBtn();
            checkInput();
            let activity = action.payload.activity;
            let conversationId = activity.conversation.id;
            document.cookie = "conversationId=" + conversationId + "; Max-Age=3600; SameSite=None; Secure";
            var cards = document.getElementsByClassName("ac-adaptiveCard");
            for (let i = 0; i < cards.length; i++) {
              //cards[i].onclick = null
              cards[i].style.pointerEvents = "none";
            }
            var buttons = document.getElementsByClassName("ac-pushButton");
            for (let i = 0; i < buttons.length; i++) {
              buttons[i].onclick = null;
              buttons[i].style.pointerEvents = "none";
            }
            if (action.payload.activity.type === "typing" || action.payload.activity.from.role === "user") {
              // clearTimeout(timer);
              // timer = setTimeout(() => {
              //     clearTimeout(timer);
              //     onReset();
              // }, 3600000);
            }
            if (action.payload.activity.type === "event") {
              streaming(activity);
            }
            if (action.payload.activity.from.id.startsWith("dl_")) {
              action.payload.activity.from.role = "user";
            }

            if (action.payload.activity.from.role === "user") {
              action.payload.activity.channelData.state = "sent";
              if (action.payload.activity.channelData.state === "sent" && action.payload.activity.type !== "typing") {
                disableKeyboard();
                disableMic();
                disableGeo();
                disableSend();
              }
            }
            if (action.payload.activity.from.role === "bot") {
              if (activity.attachments === undefined && activity.value === undefined) {
                enableSendBoxMain();
                enableInput();
                enableKeyboard();
                enableMic();
                enableGeo();
                enableSend();
              } else if (activity.attachments !== undefined && activity.attachments.length > 0 && activity.value !== undefined && activity.value !== null && activity.value.enableInput === true) {
                enableSendBoxMain();
                enableInput();
                enableKeyboard();
              } else if (activity.attachments !== undefined && activity.attachments.length > 0) {
                disableInput();
                disableSendBoxMain();
                disableKeyboard();
              }
              return next(action);
            }
          }
          return next(action);
        }),
      });
    })();
  }, [renderBotMarkdown, setSession, sourceChannel, userLocale]);
  useEffect(initConversation, [initConversation]);

  useEffect(() => {
    const keyboardClassName = "keyboard-active";
    const visualViewport = window.visualViewport;

    if (!visualViewport) return undefined;

    const updateKeyboardState = () => {
      const viewportHeightDiff = window.innerHeight - visualViewport.height;
      document.body.classList.toggle(keyboardClassName, viewportHeightDiff > 120);
    };

    updateKeyboardState();
    visualViewport.addEventListener("resize", updateKeyboardState);
    visualViewport.addEventListener("scroll", updateKeyboardState);

    return () => {
      visualViewport.removeEventListener("resize", updateKeyboardState);
      visualViewport.removeEventListener("scroll", updateKeyboardState);
      document.body.classList.remove(keyboardClassName);
    };
  }, []);

  async function fetchToken() {
    let url = "/api/directline/token";
    if (!utils.getEnv("REACT_APP_TOKEN_BASE_URL") === undefined || utils.getEnv("REACT_APP_TOKEN_BASE_URL")) url = utils.getEnv("REACT_APP_TOKEN_BASE_URL") + url;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ conversationId: getCookie("conversationId") }),
    });
    return await res.json();
  }

  async function fetchTokenSpeech() {
    let url = "/api/speech/token"; //qui per lo speech
    if (!utils.getEnv("REACT_APP_TOKEN_BASE_URL") === undefined || utils.getEnv("REACT_APP_TOKEN_BASE_URL")) url = utils.getEnv("REACT_APP_TOKEN_BASE_URL") + url;
    const res = await fetch(url, {
      method: "GET",
    });
    return await res.json();
  }

  function getUserCoordinates() {
    return new Promise((resolve, reject) => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    resolve({ latitude, longitude });
                },
                (error) => {
                    reject(error);
                }
            );
        } else {
            reject(new Error("Geolocation is not supported by this browser."));
        }
    });
  };

  //   function checkInput() {
  //   let input = document.querySelector(".webchat__send-box-text-box__input")
  //   let sendBtn = document.querySelector(".webchat__send-button")
  //   let micBtn = document.querySelector(".webchat__microphone-button")
  //   if (input.value.trim() !== '' && input && micBtn && sendBtn) {
  //     micBtn.style.display = 'none';
  //     sendBtn.style.display = 'flex';
  //   } else if (input && micBtn && sendBtn) {
  //     micBtn.style.display = 'flex';
  //     sendBtn.style.display = 'none';
  //   }
  // }

  function checkInput() {
    let input = document.querySelector(".webchat__send-box-text-box__input");
    let sendBtn = document.querySelector(".webchat__send-button");
    let micBtn = document.querySelector(".webchat__microphone-button");

    if (input && sendBtn && micBtn && input.value.trim() !== "") {
      micBtn.classList.remove("fade-in");
      micBtn.classList.add("fade-out");
      setTimeout(() => {
        micBtn.style.display = "none";
        sendBtn.style.display = "flex";
        sendBtn.classList.remove("fade-out");
        sendBtn.classList.add("fade-in");
      }, 100);
    } else if (input && micBtn && sendBtn) {
      sendBtn.classList.remove("fade-in");
      sendBtn.classList.add("fade-out");

      setTimeout(() => {
        sendBtn.style.display = "none";
        micBtn.style.display = "flex";
        micBtn.classList.remove("fade-out");
        micBtn.classList.add("fade-in");
      }, 100);
    }
  }

  const handleSendMessage = () => {
    if (session && session.store) {
      let messageInput = document.querySelector(".webchat__send-box-text-box__input");
      if (messageInput) {
        const messageText = messageInput.value.trim();
        if (messageText) {
          session.store.dispatch({
            type: "WEB_CHAT/SEND_MESSAGE",
            payload: {
              text: messageText,
            },
          });
          session.store.dispatch({
            type: 'WEB_CHAT/SET_SEND_BOX',
            payload: {
                text: ""
            },
            userLocale: userLocale
          });
        }
      }
    }
  };

  const handleSendActivity = () => {
    if (session && session.store) {
      getUserCoordinates()
        .then((coords) => {
          session.store.dispatch({
            type: "WEB_CHAT/SEND_MESSAGE",
            payload: {
              text: "📍",
              channelData: { type:"LOCATION",latitude: coords.latitude, longitude: coords.longitude }
            },
          });
        })
        .catch((error) => {
          console.error("Geolocation error:", error);
          setDialogTitle("Permission Required");
          setDialogMessage("Please grant permission to access your location to use this feature.");
          setShowDialog(true);
        });
    }
  };

  function addSendBtn() {
    if (!document.querySelector(".webchat__send-box__main > .webchat__send-button")) {
      const sendButtonDiv = document.createElement('div');
      const referenceDiv = document.querySelector('[aria-controls="webchatSendBoxMicrophoneButton"]');
      sendButtonDiv.className = 'webchat__send-button webchat__send-box__button webchat__send-box__button--align-stretch';
      if (referenceDiv) {
        for (let className of referenceDiv.classList) {
          if (className.startsWith('webchat--css')) {
            sendButtonDiv.classList.add(className);
          }
        }
      }
      sendButtonDiv.style.order = '3';

      const sendButton = document.querySelector('#sendBtn');
      sendButton.className = 'webchat__icon-button webchat__icon-button--stretch';
      if (referenceDiv) {
        let referenceBtn = referenceDiv.querySelector("button")
        if (referenceBtn)
          for (let className of referenceBtn.classList) {
          if (className.startsWith('webchat--css')) {
            sendButton.classList.add(className);
          }
        }
      }
      sendButton.title = 'Send';
      sendButton.type = 'button';

      const shadeDiv = document.createElement('div');
      shadeDiv.className = 'webchat__icon-button__shade';
      
      const sendImg = document.createElement('img');
      sendImg.src = Send;
      sendImg.alt = 'Send';
      sendImg.className = 'send-img';
      
      const focusIndicatorDiv = document.createElement('div');
      focusIndicatorDiv.className = 'webchat__icon-button__keyboard-focus-indicator';

      sendButton.appendChild(shadeDiv);
      sendButton.appendChild(sendImg);
      sendButton.appendChild(focusIndicatorDiv);

      sendButtonDiv.appendChild(sendButton);

      const sendBoxMain = document.querySelector('.webchat__send-box__main');
      const micButton = document.querySelector('.webchat__microphone-button');

      if (sendBoxMain) {
          if (micButton) {
              micButton.style.order = '2';
              sendBoxMain.insertBefore(sendButtonDiv, micButton);
          } else {
              sendBoxMain.appendChild(sendButtonDiv);
          }
      } else {
          console.error("Could not find webchat__send-box__main div");
      }
    }
  }

  function addGeolocationBtn() {
    if (!document.querySelector(".webchat__send-box__main > .webchat__geolocation-button")) {
      const geoButtonDiv = document.createElement('div');
      const referenceDiv = document.querySelector('[aria-controls="webchatSendBoxMicrophoneButton"]');
      geoButtonDiv.className = 'webchat__geolocation-button webchat__send-box__button webchat__send-box__button--align-stretch';
      if (referenceDiv) {
        for (let className of referenceDiv.classList) {
          if (className.startsWith('webchat--css')) {
            geoButtonDiv.classList.add(className);
          }
        }
      }
      geoButtonDiv.style.order = '1';

      const geoButton = document.querySelector('#geoLocationBtn');
      geoButton.className = 'webchat__icon-button webchat__icon-button--stretch';
      if (referenceDiv) {
        let referenceBtn = referenceDiv.querySelector("button")
        if (referenceBtn)
          for (let className of referenceBtn.classList) {
          if (className.startsWith('webchat--css')) {
            geoButton.classList.add(className);
          }
        }
      }
      geoButton.title = 'Geolocation';
      geoButton.type = 'button';

      const shadeDiv = document.createElement('div');
      shadeDiv.className = 'webchat__icon-button__shade';
      
      const sendImg = document.createElement('img');
      sendImg.src = Globe;
      sendImg.alt = 'Globe';
      sendImg.className = 'geo-img';
      sendImg.draggable = false;
      
      const focusIndicatorDiv = document.createElement('div');
      focusIndicatorDiv.className = 'webchat__icon-button__keyboard-focus-indicator';

      geoButton.appendChild(shadeDiv);
      geoButton.appendChild(sendImg);
      geoButton.appendChild(focusIndicatorDiv);

      geoButtonDiv.appendChild(geoButton);

      const sendBoxMain = document.querySelector('.webchat__send-box__main');
      const micButton = document.querySelector('.webchat__microphone-button');

      if (sendBoxMain) {
          if (micButton) {
              micButton.style.order = '2';
              sendBoxMain.insertBefore(geoButtonDiv, micButton);
          } else {
              sendBoxMain.appendChild(geoButtonDiv);
          }
      } else {
          console.error("Could not find webchat__send-box__main div");
      }
    }
  }


  function getCookie(name) {
    var nameEQ = name + "=";
    var ca = document.cookie.split(";");
    for (var i = 0; i < ca.length; i++) {
      var c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  function disableSendBoxMain() {
    let input = document.getElementsByClassName("webchat__send-box__main");
    if (input[0] !== null && input[0] !== undefined) input[0].style.display = "none";
    if (input[1] !== null && input[1] !== undefined) input[1].style.display = "none";
  }

  function disableInput() {
    let input = document.getElementsByClassName("webchat__send-box");
    if (input[0] !== null && input[0] !== undefined) input[0].style.display = "none";
  }

  function enableInput() {
    let input = document.getElementsByClassName("webchat__send-box");
    // let ls = document.getElementsByClassName("webchat__send-box__main");
    // const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    // const img = document.createElementNS('http://www.w3.org/2000/svg', 'image');
    // //const child = ls[0].children[1].getElementsByTagName('svg');
    // const form = ls[0].children[0]
    // const children = ls[0].children[1].cloneNode(true).children[0]
    // const child = children.getElementsByTagName('svg');
    // img.setAttributeNS('http://www.w3.org/1999/xlink', 'href', sendButton);
    // img.setAttribute('height', '28');
    // img.setAttribute('width', '28');
    // // img.src= sendButton;
    // svg.setAttribute('height', '28');
    // svg.setAttribute('width', '28');
    // svg.appendChild(img);
    // //children[0].replaceWith(svg)
    // child[0].replaceWith(svg)
    // children.className = ""
    // children.classList.add(
    //     "webchat__icon-button",
    //     "webchat__icon-button--stretch",
    //     "webchat__send-button",
    //     "webchat__send-box__button",
    //     "webchat__send-box__button--align-stretch"
    // );
    // children.title = "Invia";
    // children.addEventListener('click', submit);
    // ls[0].appendChild(children)
    input[0].style.display = "block";
  }

  function enableSendBoxMain() {
    let input = document.getElementsByClassName("webchat__send-box__main");
    input[0].style.display = "flex";
  }

  function disableKeyboard() {
    let input = document.getElementsByClassName("webchat__send-box-text-box__input");
    let button = document.getElementsByClassName("webchat__send-button");
    if (input[0]) input[0].disabled = true;
    if (button[0]) button[0].disabled = true;
  }

  function enableKeyboard() {
    let input = document.getElementsByClassName("webchat__send-box-text-box__input");
    let button = document.getElementsByClassName("webchat__send-button");
    if (input[0]) input[0].disabled = false;
    if (button[0]) button[0].disabled = false;
  }
  function disableMic() {
    let input = document.getElementsByClassName("webchat__send-box-text-box__input");
    let mic = document.getElementsByClassName("webchat__microphone-button__button");
    if (input[0]) input[0].disabled = true;
    if (mic[0]) {
      mic[0].disabled = true;
    }
  }

  function enableMic() {
    let input = document.getElementsByClassName("webchat__send-box-text-box__input");
    let mic = document.getElementsByClassName("webchat__microphone-button__button");
    if (input[0]) input[0].disabled = false;
    if (mic[0]) mic[0].disabled = false;
  }

  function disableGeo() {
    let geoBtn = document.getElementsByClassName("webchat__geolocation-button");
    if (geoBtn[0]) {
      geoBtn[0].children[0].disabled = true
    }
  }

  function enableGeo() {
    let geoBtn = document.getElementsByClassName("webchat__geolocation-button");
    if (geoBtn[0]) {
      geoBtn[0].children[0].disabled = false
    }
  }

  function disableSend() {
    let geoBtn = document.getElementsByClassName("webchat__send-button");
    if (geoBtn[0]) {
      geoBtn[0].children[0].disabled = true
    }
  }

  function enableSend() {
    let geoBtn = document.getElementsByClassName("webchat__send-button");
    if (geoBtn[0]) {
      geoBtn[0].children[0].disabled = false
    }
  }

  function onReset(e) {
    document.cookie = "conversationId=; Max-Age=-99999999; SameSite=None; Secure";
    // var maxId = setTimeout(function(){}, 0);
    // for(var i=0; i < maxId; i+=1) {
    //     clearTimeout(i);
    // }
    initConversation();
  }

  function streaming(activity) {
    disableKeyboard();
    disableMic();
    disableGeo();
    disableSend();
    let botMessage = document.getElementsByClassName("webchat__stacked-layout__message-row");
    if (botMessage && botMessage[botMessage.length - 1]) {
      let lastMessage = botMessage[botMessage.length - 1].children[1];
      if (lastMessage) {
        let child = lastMessage.children[1]; // Assuming the content element is the second child
        // Check if child exists before accessing its class
        if (child && child.classList.contains("webchat__bubble__content")) {
          let content = child; // Assign the element to the content variable
          if (activity.value.text !== "") {
            content.querySelector("div").innerHTML = renderBotMarkdown(activity.value.text);
          }
        } else {
          // Handle the case where the second child doesn't have the desired class
          console.warn("Element with class 'webchat__bubble__content' not found in the second child.");
        }
      } else {
        // Handle the case where no message elements are found
        console.warn("No elements with class 'webchat__stacked-layout__message-row' found.");
      }
    }
    if (activity.value.end) {
      enableKeyboard();
      enableMic();
      enableGeo();
      enableSend();
    }
  }

  // const middleware = () => next => action => {
  //         switch (action.activity.type) {
  //             case 'event':
  //                 useActivities
  //                 return next(action)
  //
  //             default:
  //                 return next(action);
  //         }
  //     };
  return (
    <>
      {session ? (
        <Container fluid="true" className="flex-fill">
          <Row className="m-0 p-0 fixMobile">
            <Col className=" m-0 p-0">
              <Header onReset={(e) => onReset(e)}></Header>
              <ReactWebChat
                className="webchat"
                userID={getCookie("userId")}
                directLine={session.directLine}
                key={session.key}
                store={session.store}
                renderMarkdown={renderBotMarkdown}
                styleOptions={styleSet}
                locale={userLocale}
                //activityMiddleware={middleware}
                sendTypingIndicator={true}
                webSpeechPonyfillFactory={session.webSpeechPonyfillFactory}
                selectVoice={session.selectVoice}
              />
            </Col>
            <button onClick={handleSendActivity} className="d-none" id="geoLocationBtn"></button>
            <button onClick={handleSendMessage} className="d-none" id="sendBtn"></button>
          </Row>

          <Modal centered show={showDialog} onHide={() => setShowDialog(false)}>
            <Modal.Header closeButton>
            <Modal.Title>{dialogTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{dialogMessage}</Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowDialog(false)}>
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      ) : (
        <div>Caricamento...</div>
      )}
    </>
  );
};

export default WebChat;
